import {useCallback, useEffect} from 'react';
import {useFocusEffect} from '@react-navigation/native';

import ChessRestClient from '@/Api/ChessRestClient';
import {getLocalStorage} from '@/Utils/CommonUtils';
import {event} from '@/Utils/GAEvents';
import {useJourneyStore} from '@/Store/journeyStore';
import {dropExpiredCards} from '@/Utils/journeyGates';

export type JourneyTrigger =
  | 'cold_start'
  | 'visibility_active'
  | 'dashboard_focus'
  | 'game_end';

export type JourneyAckAction = 'impression' | 'dismiss' | 'cta_tap';

// Coalescing knobs. No setInterval anywhere -- pull fires ONLY from the four
// lifecycle call sites below (useJourneySync, useJourneyDashboardFocusSync,
// scheduleJourneyGameEndSync), each gated by its own rule.
const DEBOUNCE_MS = 750;
const VISIBILITY_MIN_INTERVAL_MS = 30_000;
const DASHBOARD_FOCUS_STALE_MS = 60_000;
const GAME_END_RECENT_ENVELOPE_MS = 10_000;

// Module-level singleton state -- deliberately NOT per-component, so calls
// from unrelated call sites (Application.tsx cold start, Dashboard's focus
// effect, PlayChess's post-game hook) coalesce into the same debounce window
// / in-flight request.
let debounceHandle: ReturnType<typeof setTimeout> | null = null;
let isFetching = false;
let hasPendingCall = false;
let pendingTriggers: JourneyTrigger[] = [];

const hasAccessToken = async (): Promise<boolean> => {
  try {
    const token = await getLocalStorage('access_token');
    return !!token;
  } catch {
    return false;
  }
};

// Per-trigger min-interval guards. Each trigger has its own rule (spec'd per
// lifecycle event), checked BEFORE the debounce/in-flight machinery so a
// gated-out call never resets the shared debounce window. This ALSO doubles
// as the visibilitychange/focus dedupe: both listeners call fetchJourney
// with the same 'visibility_active' trigger, so whichever fires first marks
// lastSyncByTrigger and the other is skipped by this same guard.
const shouldSkip = (trigger: JourneyTrigger): boolean => {
  const {fetchedAt, lastSyncByTrigger} = useJourneyStore.getState();
  const now = Date.now();
  switch (trigger) {
    case 'dashboard_focus':
      // staleTime 60s: skip if something already refreshed the envelope
      // recently, regardless of which trigger did it.
      return now - fetchedAt < DASHBOARD_FOCUS_STALE_MS;
    case 'visibility_active':
      return (
        now - (lastSyncByTrigger.visibility_active || 0) <
        VISIBILITY_MIN_INTERVAL_MS
      );
    case 'game_end':
      // Skip if an envelope arrived in the last 10s.
      return now - fetchedAt < GAME_END_RECENT_ENVELOPE_MS;
    case 'cold_start':
    default:
      return false;
  }
};

const markTriggerAttempt = (trigger: JourneyTrigger): void => {
  const now = Date.now();
  useJourneyStore.setState(state => ({
    lastSyncByTrigger: {...state.lastSyncByTrigger, [trigger]: now},
  }));
};

const performFetch = async (triggers: JourneyTrigger[]): Promise<void> => {
  try {
    if (!(await hasAccessToken())) {
      // No session yet (cold start on the logged-out home/login route,
      // guest, etc.) -- never hit an authenticated endpoint without a token,
      // since ChessRestClient's response interceptor force-logs-out on 401.
      return;
    }

    const knownVersion = useJourneyStore.getState().v;
    const response = await ChessRestClient.get('journey/v1/next_action', {
      params: {known_version: knownVersion},
    });
    const data = response?.data;

    if (!data || data.enabled === false) {
      // Feature disabled / not rolled out for this user -- render nothing.
      return;
    }

    if (data.unchanged) {
      useJourneyStore.setState({fetchedAt: Date.now()});
      return;
    }

    // Anything else is expected to be a full envelope. Defensively require
    // the two fields everything downstream depends on before trusting it --
    // the backend is shared with mobile and evolving independently of this
    // client, so a malformed/partial payload must fall through to "no
    // update" rather than corrupt the store.
    if (!Array.isArray(data.cards) || typeof data.v !== 'number') {
      return;
    }

    useJourneyStore.getState().applyEnvelope(data);
    // One event per network fetch, not one per coalesced trigger -- multiple
    // lifecycle events can fold into the same debounced request.
    event({action: 'journey_sync', params: {triggers: triggers.join(',')}});
  } catch (error) {
    // ANY error / 404 -- swallow. The feature renders nothing and never
    // blocks or crashes the dashboard; the next natural lifecycle trigger
    // will retry, no manual retry loop here.
    console.warn('[journey] sync failed:', error);
  }
};

const runFetch = (): void => {
  if (isFetching) {
    // A request is already in flight -- fold this call into it instead of
    // firing a second one; run once more right after it settles so the
    // trigger that arrived mid-request isn't dropped.
    hasPendingCall = true;
    return;
  }
  const triggers = pendingTriggers;
  pendingTriggers = [];
  isFetching = true;
  performFetch(triggers).finally(() => {
    isFetching = false;
    if (hasPendingCall) {
      hasPendingCall = false;
      runFetch();
    }
  });
};

/**
 * The one coalesced entry point for every journey pull. Safe to call from
 * anywhere (component or not) and as often as lifecycle events fire --
 * gating, debouncing and in-flight dedup all happen internally.
 */
export const fetchJourney = (trigger: JourneyTrigger): void => {
  if (shouldSkip(trigger)) {
    return;
  }

  markTriggerAttempt(trigger);
  pendingTriggers.push(trigger);

  if (debounceHandle) {
    clearTimeout(debounceHandle);
  }
  debounceHandle = setTimeout(() => {
    debounceHandle = null;
    runFetch();
  }, DEBOUNCE_MS);
};

/** Fire-and-forget ack ping; never awaited, never throws into the caller. */
export const postJourneyAck = (
  itemId: string,
  action: JourneyAckAction,
): void => {
  (async () => {
    try {
      if (!(await hasAccessToken())) {
        return;
      }
      await ChessRestClient.post('journey/v1/ack', {
        item_id: itemId,
        action,
      });
    } catch (error) {
      console.warn('[journey] ack failed:', error);
    }
  })();
};

// Synchronously purges expired cards from the store's envelope. Shared by
// both the visibilitychange and focus listeners below -- tab timers are
// throttled/frozen while hidden/blurred, so a card's own auto-hide
// setTimeout can't be trusted to have already fired by the time the tab
// comes back.
const purgeExpiredCards = (): void => {
  const {envelope, serverClockOffset} = useJourneyStore.getState();
  if (!envelope?.cards?.length) {
    return;
  }
  const now = Date.now();
  const filtered = dropExpiredCards(envelope.cards, serverClockOffset, now);
  if (filtered.length !== envelope.cards.length) {
    useJourneyStore.setState({envelope: {...envelope, cards: filtered}});
  }
};

/**
 * Mount ONCE near the app root (Application.tsx). Fires the cold_start pull
 * and wires the visibility_active listeners.
 *
 * Web has no AppState -- the equivalent "did the user come back to this tab"
 * signal is document visibilitychange (-> visible) OR window focus (a tab
 * switch fires the former, alt-tab back to an already-visible browser window
 * fires the latter; neither alone covers both). Both call fetchJourney with
 * the SAME 'visibility_active' trigger, so shouldSkip's own 30s min-interval
 * guard (above) naturally dedupes the pair when both fire back-to-back for
 * the same resume.
 */
export const useJourneySync = (): void => {
  useEffect(() => {
    // Fire-and-forget in parallel -- never awaited by anything.
    fetchJourney('cold_start');
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      purgeExpiredCards();
      fetchJourney('visibility_active');
    };

    const handleWindowFocus = () => {
      purgeExpiredCards();
      fetchJourney('visibility_active');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);
};

/**
 * Mount in the Dashboard screen. staleTime is enforced inside fetchJourney's
 * own dashboard_focus guard (skip if fetchedAt < 60s ago), so this just needs
 * to ask on every focus.
 */
export const useJourneyDashboardFocusSync = (): void => {
  useFocusEffect(
    useCallback(() => {
      fetchJourney('dashboard_focus');
    }, []),
  );
};

/**
 * Imperative call. Call from the same post-game-modal-close call site, after
 * a game ends. Random 0-3s jitter so it doesn't compete with the end-game
 * modal's own transition; fetchJourney's own game_end guard skips it if an
 * envelope already arrived in the last 10s.
 */
export const scheduleJourneyGameEndSync = (): void => {
  const jitterMs = Math.random() * 3000;
  setTimeout(() => {
    fetchJourney('game_end');
  }, jitterMs);
};
